import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { getFile } from '../../services/fileService';
import Fallback from '../../components/common/Fallback';
import PrintReceipt from '../../components/prints/Receipt';
import { generatePdfBlobUrlFromElement } from '../../utils/pdfPreview';

const ReceiptPrintPage = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const printRef = useRef(null);

  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');
  const [generatingPdf, setGeneratingPdf] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setNotFound(false);
    try {
      const data = await getFile(id);
      setFile(data);
    } catch (err) {
      console.error(err);
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const selectedPaymentIds = useMemo(() => {
    return (searchParams.get('paymentIds') || '')
      .split(',')
      .map((part) => Number(part.trim()))
      .filter((n) => !Number.isNaN(n));
  }, [searchParams]);

  const payments = useMemo(() => {
    const fromApi = file?.payments || [];
    if (!Array.isArray(fromApi) || selectedPaymentIds.length === 0) return [];

    const byId = new Set(selectedPaymentIds.map((n) => Number(n)));
    return fromApi
      .filter((p) => byId.has(Number(p?.id)))
      .sort((a, b) => {
        const aTime = Date.parse(a?.date || '');
        const bTime = Date.parse(b?.date || '');
        if (!Number.isNaN(aTime) && !Number.isNaN(bTime) && aTime !== bTime) return aTime - bTime;
        return (Number(a?.id) || 0) - (Number(b?.id) || 0);
      });
  }, [file, selectedPaymentIds]);

  useEffect(() => {
    if (loading) return;
    if (!file || selectedPaymentIds.length === 0 || payments.length === 0) {
      setNotFound(true);
    }
  }, [loading, file, selectedPaymentIds, payments]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const generatePdfPreview = useCallback(async () => {
    if (!printRef.current) return;
    setGeneratingPdf(true);
    try {
      const nextUrl = await generatePdfBlobUrlFromElement(printRef.current);
      setPdfUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return nextUrl;
      });
    } finally {
      setGeneratingPdf(false);
    }
  }, []);

  useEffect(() => {
    if (!loading && !notFound && file && payments.length > 0) {
      const timer = setTimeout(() => {
        generatePdfPreview();
      }, 120);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [loading, notFound, file, payments, generatePdfPreview]);

  useEffect(() => {
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  }, [pdfUrl]);

  if (loading || (!file && !notFound)) {
    return (
      <div className="page-wrapper">
        {loading && <p>Cargando recibo...</p>}
      </div>
    );
  }

  if (notFound || !file || payments.length === 0) {
    return (
      <div className="page-wrapper">
        <Fallback title="Recibo no encontrado" description="Los pagos seleccionados no existen en esta ficha." />
      </div>
    );
  }

  return (
    <>
      {/* {generatingPdf && <p>Generando previsualizacion PDF...</p>} */}
      {!generatingPdf && pdfUrl && (
        <iframe
          title="Previsualizacion recibo PDF"
          src={pdfUrl}
          style={{
            width: '100%',
            height: '100%',
            border: '1px solid #ddd',
            borderRadius: 8,
            background: '#fff'
          }}
        />
      )}

      <div
        aria-hidden
        style={{
          position: 'fixed',
          left: '-10000px',
          top: 0,
          width: 794,
          height: '100%',
          background: '#ffffff'
        }}
      >
        <div ref={printRef}>
          <PrintReceipt file={file} payments={payments} />
        </div>
      </div>
    </>
  );
};

export default ReceiptPrintPage;

